import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Box, Center, Flex, HStack, Stack, Text } from '@chakra-ui/react';
import { isEmpty } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { components } from 'react-select';
import AsyncSelect from 'react-select/async';
import { ValueContainerProps } from 'react-select/dist/declarations/src/components/containers';
import { colorMap, customTheme } from 'theme';

import { defaultSelectStyles, Select } from 'components/DataEntry';
import { DropdownIndicator } from 'components/DataEntry/Search';
import { SearchIcon } from 'components/Icons';
import { getIcon } from 'components/Icons/Components';
import { useQuerySearchTerms } from 'containers/Visibility/SecurityGraphNext/hooks/useQuerySearchTerms';
import { selectSecurityGraphNext } from 'containers/Visibility/SecurityGraphNext/selectors';
import { actions } from 'containers/Visibility/SecurityGraphNext/slice';

const formatGroupLabel = data => (
  <Box
    borderBottom="1px solid"
    borderColor="gray.200"
    pb={1}
    fontSize={12}
    color="gray.250"
  >
    {data.label}
  </Box>
);

const MenuList = props => {
  return (
    <components.MenuList {...props}>
      <Stack spacing={1}>{props.children}</Stack>
    </components.MenuList>
  );
};

const CustomOption = props => {
  const { data } = props;

  return (
    <components.Option {...props}>
      <Stack _hover={{ cursor: 'pointer' }}>
        <HStack>
          <HStack flex={1}>
            <Center p={1} w={6} h={6} color="primary">
              {getIcon(
                data.value?.native_resource || data.value?.agnostic_resource,
              )}
            </Center>
            <Text>{data.label ?? '-'}</Text>
          </HStack>
          <Flex
            px={3}
            bg={
              data.value?.is_custom_policy ? 'green.300' : colorMap.primary(300)
            }
            borderRadius={4}
            color="white"
            opacity={0.7}
            fontSize="xs"
            w="70px"
          >
            <Center w="full">
              {data.value?.is_custom_policy ? 'Custom' : 'Ariksa'}
            </Center>
          </Flex>
        </HStack>
        {/*<Text px={8} color={'#aaa'}>
          {data.value.query ?? '-'}
        </Text>*/}
      </Stack>
    </components.Option>
  );
};

const CustomValueContainer = (props: ValueContainerProps) => {
  return (
    components.ValueContainer && (
      <components.ValueContainer {...props}>
        <HStack w="full" h="full" overflow="auto">
          <Box color={'gray'} fontSize={'md'} flexShrink={0}>
            <SearchIcon />
          </Box>
          {props.children}
        </HStack>
      </components.ValueContainer>
    )
  );
};

const securityGraphSearchStyles = {
  ...defaultSelectStyles(),
  container: (...args) => ({
    ...defaultSelectStyles().container(...args),
    minHeight: 32,
    backgroundColor: 'white',
    borderRadius: customTheme.radii.fieldBorderRadius,
    overflow: 'hidden',
  }),
  control: (styles, props) => ({
    ...defaultSelectStyles().control(styles, props),
    backgroundColor: 'white',
    overflow: 'hidden',
  }),
  valueContainer: (styles, props) => ({
    ...defaultSelectStyles().valueContainer(styles, props),
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  }),
  menu: provided => ({
    ...defaultSelectStyles().menu(provided),
    borderRadius: customTheme.radii.fieldBorderRadius,
    overflow: 'hidden',
  }),
  menuList: provided => ({
    ...provided,
    padding: 0,
    maxHeight: 550,
  }),
  groupHeading: provided => ({
    ...defaultSelectStyles().groupHeading(provided),
    padding: '8px 12px 4px',
  }),
  option: (provided, props) => ({
    ...defaultSelectStyles().option(provided, props),
    padding: '8px 12px',
  }),
  input: provided => ({
    ...provided,
    color: customTheme.colors.gray['400'],
    opacity: 1,
  }),
  menuPortal: provided => ({
    ...defaultSelectStyles().menuPortal(provided),
    zIndex: 9999,
  }),
};

export const SecurityGraphSearch = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearchCleared, setIsSearchCleared] = useState(false);
  const dispatch = useDispatch();
  const {
    recentSearchOptions,
    searchTermOptions,
    getStartedSearchOptions,
    currentSearchQuery,
  } = useSelector(selectSecurityGraphNext);
  const navigate = useNavigate();

  const {
    getSearchQuerySuggestion,
    isLoadingSuggestion,
  } = useQuerySearchTerms();

  const selectedSearchOption = useMemo(() => {
    if (isSearchCleared || !currentSearchQuery) {
      return null;
    }

    return {
      label:
        (currentSearchQuery.description || currentSearchQuery.query) ?? '-',
      value: currentSearchQuery as any,
    };
  }, [currentSearchQuery, isSearchCleared]);

  const defaultSearchOptions = useMemo(() => {
    const options: any[] = [];
    if (!isEmpty(searchTermOptions)) {
      options.push({
        label: 'AI-Driven SUGGESTIONS',
        options: searchTermOptions,
      });
    }

    if (!isEmpty(recentSearchOptions.data)) {
      options.push({
        label: <Box color="primary">Recent Searches (last 5)</Box>,
        options: recentSearchOptions.data.map(s => ({
          label: s.description || s.name,
          value: { uuid: s.id, ...s },
        })),
      });
    }

    if (!isEmpty(getStartedSearchOptions.data)) {
      options.push({
        label: <Box color="primary">Get Started</Box>,
        options: getStartedSearchOptions.data.map(s => ({
          label: s.description || s.name,
          value: { uuid: s.id, ...s },
        })),
      });
    }

    return options;
  }, [recentSearchOptions, searchTermOptions, getStartedSearchOptions]);

  const handleOnClear = useCallback(() => {
    setSearchTerm('');
    setIsSearchCleared(true);
    dispatch(actions.resetSearchQuery());
    dispatch(actions.updateRedirectInfo(null));
    navigate('/visibility/security-graph');
  }, [dispatch, navigate]);

  const handleOnChange = useCallback(
    selected => {
      if (!selected) {
        handleOnClear();
        return;
      }

      setSearchTerm('');
      setIsSearchCleared(false);
      dispatch(actions.updateQueryChanged(true));
      dispatch(actions.updateSelectedSearchQueryTerm(selected?.value ?? null));
      // reset the search query and filters when a new search is initiated
      navigate('/visibility/security-graph');
    },
    [dispatch, handleOnClear, navigate],
  );

  const handleOnInputChange = useCallback((value, actionMeta) => {
    if (actionMeta.action === 'input-change') {
      setSearchTerm(value);
    }

    return value;
  }, []);

  useEffect(() => {
    dispatch(
      actions.getRecentSearchOptions({
        q: { recent: 5 },
      }),
    );
    dispatch(
      actions.getGetStartedSearchOptions({
        q: { getStarted: true, recent: 5 },
      }),
    );
  }, [dispatch]);

  return (
    <Stack flex={1} spacing={2}>
      <Box w="full">
        <Select
          async
          noOptionsMessage={() => 'Please type to search...'}
          cacheOptions
          options={defaultSearchOptions}
          defaultOptions={defaultSearchOptions}
          value={selectedSearchOption}
          inputValue={searchTerm}
          onInputChange={handleOnInputChange}
          loadOptions={getSearchQuerySuggestion}
          onChange={handleOnChange}
          placeholder={'Search...'}
          formatGroupLabel={formatGroupLabel}
          // isDisabled={viewType}
          components={{
            Option: CustomOption,
            ValueContainer: CustomValueContainer as any,
            MenuList,
            DropdownIndicator: props => (
              <DropdownIndicator
                {...props}
                onReset={handleOnClear}
              />
            ),
          }}
          isLoading={isLoadingSuggestion}
          maxMenuHeight={550}
          styles={securityGraphSearchStyles}
          menuPortalTarget={document.querySelector('body')}
        />
      </Box>
    </Stack>
  );
};
